import * as React from 'react';

import Button from '../../ui/Buttons/Button';
import {CheckBox} from '../../ui/forms/CheckBox/CheckBox';
import {Facet, FacetField} from '../search.types';
import {gaLegacyCustomEvent} from '../../../client/ga/ga-legacy.functions';
import {ProductImage} from '../../ui/ProductImage/ProductImage';
import {pushGaEvent} from '../../../client/ga/ga.functions';
import {URLBuilder} from '../../../server/tools/url-builder/url-builder.class';
import {useState} from 'react';

interface FacetLineProps {
    className?: string;
    facet: Facet;
    facetField: FacetField;
    facetIndex: number;
    isLastChecked?: boolean;
    urlBuilder: URLBuilder;
}

const CharsToClear = /[\u{0000}-\u{001F}\u{0080}-\u{FFFF}]/gu;
const QuoteSearch = /"/g;
const SlashQuoteSearch = /\\"/g;

export const FacetLine = ({className, facet, facetField, facetIndex, isLastChecked, urlBuilder}: FacetLineProps) => {
    const [tempChecked, setTempChecked] = useState(facet.checked);
    const facetCleanText = facet.text.replace(QuoteSearch, `\\"`).replace(CharsToClear, ``).trim();

    /**
     * Template
     */
    if (facetField.leftNavRender && facetField.leftNavRender === `tile`) {
        return (
            <a
                className="tw-my-2 tw-relative"
                href={
                    facet.checked
                        ? urlBuilder.withoutFilter(facet.field, decodeURIComponent(facet.value).replace(SlashQuoteSearch, `"`))
                        : urlBuilder.withFilter(facet.field, facet.value.replace(SlashQuoteSearch, `"`))
                }
                onClick={() => {
                    gaLegacyCustomEvent({
                        eventAction: `Tile Facet ${facet.checked ? 'Off' : 'On'}`,
                        eventCategory: `Faceted Search`,
                        eventLabel: `${facetField.name}:${facetCleanText}`,
                    });
                }}
            >
                <Button
                    className={`!tw-mr-2 !tw-mb-2 !tw-rounded-0 !tw-w-[21.5%] !tw-text-sm !tw-py-2 !tw-px-0 !tw-overflow-hidden  ${
                        facet.checked ? ' !tw-border-[3px] !tw-border-[#424242]' : ''
                    }`}
                    size="sm"
                    variant="outline-secondary"
                >
                    {facet.text.trim()}
                </Button>
            </a>
        );
    }
    if (facetField.type && facetField.type === `VisualAttribute`) {
        return (
            <li className="tw-list-none tw-border-t-0 tw-align-top tw-inline-block tw-w-[31.5%] tw-my-0 tw-mx-[0.75%]">
                <a
                    className="tw-mb-2 tw-relative tw-block tw-text-center"
                    href={
                        facet.checked
                            ? urlBuilder.withoutFilter(facet.field, decodeURIComponent(facet.value).replace(SlashQuoteSearch, `"`))
                            : urlBuilder.withFilter(facet.field, facet.value.replace(SlashQuoteSearch, `"`))
                    }
                    onClick={() => {
                        gaLegacyCustomEvent({
                            eventAction: `Visual Attribute Facet ${facet.checked ? 'Off' : 'On'}`,
                            eventCategory: `Faceted Search`,
                            eventLabel: `${facetField.name}:${facetCleanText}`,
                        });
                        pushGaEvent(`ga4_facet_search`, {
                            click_action: facet.checked ? `remove_facet` : `add_facet`,
                            click_category_1: `filter_menu`,
                            click_category_2: facetField.name,
                            click_path: location.pathname,
                            click_type: `visual_attributes_facet`,
                            selection_name: `${facetField.name}: ${facet.text.trim()}`,
                        });
                    }}
                >
                    <ProductImage
                        altText={facet.text.trim().toLowerCase()}
                        className="tw-max-w-full tw-h-auto"
                        containerClass={`tw-border tw-relative tw-p-0.5 tw-flex tw-justify-center tw-items-center ${
                            facet.checked
                                ? 'active active:!tw-border-[3px] active:!tw-border-[#424242] active:after:tw-content-[""] active:after:tw-bg-center active:after:tw-rounded-[50%] active:after:tw-absolute active:after:tw-p-[1px] active:after:tw-h-[17px] active:after:tw-right-[-10px] active:after:tw-top-[-8px] active:after:tw-w-[17px]'
                                : ''
                        }`}
                        size={92}
                        src={facet.image}
                        srcIncludesImgThumbsPath
                        style={facet.checked ? {backgroundImage: `url(../../../../dimg/check-circle@2x.png)`, backgroundSize: 17} : {}}
                    />
                    <span className="tw-text-gray-700 tw-text-sm hover:tw-underline">
                        {facet.text.trim()} ({facet.count})
                    </span>
                </a>
            </li>
        );
    }
    if (facetField.type && facetField.type === `GuidedNav`) {
        return (
            <li className="tw-list-none">
                <a
                    className="tw-text-base tw-leading-7"
                    href={
                        facet.checked
                            ? urlBuilder.withoutFilter(facet.field, decodeURIComponent(facet.value).replace(SlashQuoteSearch, `"`))
                            : urlBuilder.withFilter(facet.field, facet.value.replace(SlashQuoteSearch, `"`))
                    }
                    onClick={() => {
                        gaLegacyCustomEvent({
                            eventAction: `Guided Navigation Facet ${facet.checked ? 'Off' : 'On'}`,
                            eventCategory: `Faceted Search`,
                            eventLabel: `${facetField.name}:${facetCleanText}`,
                        });
                    }}
                >
                    {facet.text.trim()} ({facet.count})
                </a>
            </li>
        );
    }
    return (
        <>
            <li className={`tw-my-4 tw-list-none ${className ? ' ' + className : ''}`}>
                <a
                    className="tw-text-black hover:tw-text-black hover:tw-no-underline"
                    href={
                        facet.checked
                            ? urlBuilder.withoutFilter(facet.field, decodeURIComponent(facet.value).replace(SlashQuoteSearch, `"`))
                            : urlBuilder.withFilter(facet.field, facet.value.replace(SlashQuoteSearch, `"`))
                    }
                    onClick={() => {
                        gaLegacyCustomEvent({
                            eventAction: `List Facet ${facet.checked ? 'Off' : 'On'}`,
                            eventCategory: `Faceted Search`,
                            eventLabel: `${facetField.name}:${facetCleanText}`,
                        });
                        pushGaEvent(`ga4_facet_search`, {
                            click_action: facet.checked ? `remove_facet` : `add_facet`,
                            click_category_1: `filter_menu`,
                            click_category_2: facetField.name,
                            click_path: location.pathname,
                            click_type: `list_facet`,
                            selection_name: `${facetField.name}: ${facet.text.trim()}`,
                        });
                        setTempChecked(!tempChecked);
                    }}
                    tabIndex={-1}
                >
                    <CheckBox
                        alias={`facet_checkbox_${facetIndex}_${facet.field}_${facet.value}`}
                        checked={tempChecked}
                        defaultChecked={facet.checked}
                        label={`${facet.text.trim()} (${facet.count})`}
                        labelClass="before:!tw-w-5 before:!tw-h-5 after:!tw-w-5 after:!tw-h-5 after:!tw-top-0.5 before:!tw-top-0.5 before:!tw-pr-1 !tw-text-base"
                        name={`facet_checkbox_${facetIndex}_${facet.field}_${facet.value}`}
                        smallCheckbox={true}
                    />
                </a>
            </li>
            {isLastChecked && <div className="tw-my-4 tw-border-t"></div>}
        </>
    );
};
